import { useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronRight, Bitcoin, Warehouse, Lock, Building2, Shield, Users, CheckCircle2, XCircle } from 'lucide-react';
import { useHiddenAssetSignals, useRunHiddenAssetScan, useHiddenAssetDecision } from '../../hooks/useTransactions';
import type { Transaction } from '../../types';
import { formatTxnDate } from '../../utils/dates';
import { formatAccountName } from '../../utils/accounts';
import { money } from '../../lib/money';
import { Button, Card, EmptyState, Money, Skeleton, Tag, type TagTone } from '../ui';

interface AssetSignalsViewProps {
  caseId: string;
  accountFilters?: string[];
}

const CATEGORY_META: Record<string, {
  icon: React.ReactNode;
  tone: TagTone;
  color: string;
  bg: string;
  border: string;
  description: string;
}> = {
  'Crypto Exchange': {
    icon: <Bitcoin size={16} />,
    tone: 'warn',
    color: 'text-warn',
    bg: 'bg-warn-soft',
    border: 'border-warn',
    description: 'Transfers to or from cryptocurrency exchanges. Worth checking whether any digital-asset holdings are reflected in the estate inventory — confirm current balances before treating as an undisclosed asset.',
  },
  'Storage Unit': {
    icon: <Warehouse size={16} />,
    tone: 'warn',
    color: 'text-warn',
    bg: 'bg-warn-soft',
    border: 'border-warn',
    description: 'Recurring self-storage payments. Worth checking whether off-site personal property or stored valuables are reflected in the inventory.',
  },
  'Safe Deposit Box': {
    icon: <Lock size={16} />,
    tone: 'warn',
    color: 'text-warn',
    bg: 'bg-warn-soft',
    border: 'border-warn',
    description: 'Bank safe deposit box rental fees. Worth confirming whether a vault exists and whether its contents (jewelry, documents, cash) are reflected in the inventory.',
  },
  'Business Formation': {
    icon: <Building2 size={16} />,
    tone: 'info',
    color: 'text-info',
    bg: 'bg-info-soft',
    border: 'border-info',
    description: 'LLC or corporation formation fees may point to a business interest. Worth checking whether any related entity is reflected in the estate inventory.',
  },
  'Unknown Insurance Premium': {
    icon: <Shield size={16} />,
    tone: 'info',
    color: 'text-info',
    bg: 'bg-info-soft',
    border: 'border-info',
    description: 'Insurance payments for boats, RVs, aircraft, or specialty items. Worth checking whether the insured asset is reflected in the inventory.',
  },
  'Family/Personal Loan Repayment': {
    icon: <Users size={16} />,
    tone: 'danger',
    color: 'text-danger',
    bg: 'bg-danger-soft',
    border: 'border-danger',
    description: 'Recurring payments that resemble loan repayments to individuals. Worth confirming the underlying obligation is genuine — some claimed debts to family or friends do not hold up under scrutiny.',
  },
};

const DEFAULT_META = {
  icon: <AlertTriangle size={16} />,
  tone: 'warn' as TagTone,
  color: 'text-warn',
  bg: 'bg-warn-soft',
  border: 'border-warn',
  description: 'This transaction may be worth reviewing as a possible asset signal — verify before treating it as an undisclosed asset.',
};

// Observation-style display headings (the API/grouping keys stay the raw enum
// values; this softens only the on-screen label, matching the export wording).
const CATEGORY_LABELS: Record<string, string> = {
  'Crypto Exchange': 'Possible crypto activity',
  'Storage Unit': 'Possible off-site storage',
  'Safe Deposit Box': 'Possible safe-deposit/vault fee',
  'Business Formation': 'Possible business-entity formation',
  'Unknown Insurance Premium': 'Specialty-asset insurance premium',
  'Family/Personal Loan Repayment': 'Possible personal/family loan payment',
};

function formatStampDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// "Reviewed by user_2abc..." is meaningless — only show the reviewer when it
// reads like a person (an email), otherwise just show the date.
function reviewerLabel(reviewedBy: string | null | undefined): string | null {
  if (!reviewedBy) return null;
  return reviewedBy.includes('@') ? reviewedBy : null;
}

function SignalDecisionControls({
  txn,
  onDecide,
  isPending,
}: {
  txn: Transaction;
  onDecide: (transactionId: number, decision: 'confirmed' | 'dismissed') => void;
  isPending: boolean;
}) {
  if (txn.signal_decision) {
    const confirmed = txn.signal_decision === 'confirmed';
    const who = reviewerLabel(txn.signal_reviewed_by);
    return (
      <div className="text-right">
        <Tag
          tone={confirmed ? 'good' : 'neutral'}
          shape="pill"
          icon={confirmed ? CheckCircle2 : XCircle}
        >
          {confirmed ? 'Confirmed lead' : 'Dismissed'}
        </Tag>
        <p className="mt-1 text-2xs text-ink-faint">
          Reviewed {formatStampDate(txn.signal_reviewed_at)}{who ? ` · ${who}` : ''}
        </p>
        {txn.signal_review_note && (
          <p className="mt-1 max-w-44 truncate text-2xs italic text-ink-faint" title={txn.signal_review_note}>
            "{txn.signal_review_note}"
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Button
        size="sm"
        variant="secondary"
        disabled={isPending}
        onClick={() => onDecide(txn.id, 'confirmed')}
      >
        Confirm lead
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={isPending}
        onClick={() => onDecide(txn.id, 'dismissed')}
        className="text-ink-faint"
      >
        Dismiss
      </Button>
    </div>
  );
}

function CategoryGroup({
  category,
  transactions,
  onDecide,
  isPending,
}: {
  category: string;
  transactions: Transaction[];
  onDecide: (transactionId: number, decision: 'confirmed' | 'dismissed') => void;
  isPending: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const meta = CATEGORY_META[category] ?? DEFAULT_META;
  const total = transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);

  return (
    <div className={`overflow-hidden rounded-lg border ${meta.border}`}>
      {/* eslint-disable-next-line no-restricted-syntax -- full-width disclosure row: no ui primitive fits this layout */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`flex w-full items-center gap-3 px-5 py-4 ${meta.bg} text-left`}
      >
        <span className={meta.color}>{meta.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <span className={`text-sm font-semibold ${meta.color}`}>{CATEGORY_LABELS[category] ?? category}</span>
            <Tag tone={meta.tone} shape="pill">
              {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </Tag>
            <span className={`text-xs font-medium tabular-nums ${meta.color}`}>{money(total)}</span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-ink-soft">{meta.description}</p>
        </div>
        <span className={meta.color}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>

      {expanded && (
        <div className="divide-y divide-cream-dark bg-cream-light">
          {transactions.map((txn) => (
            <div
              key={txn.id}
              className={`flex items-start gap-4 px-5 py-3 transition-colors hover:bg-cream ${
                txn.signal_decision === 'dismissed' ? 'opacity-55' : ''
              }`}
            >
              <div className="w-24 shrink-0 text-xs text-ink-faint">
                {txn.date ? formatTxnDate(txn.date) : '—'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink">{txn.description}</p>
                {txn.merchant_name && txn.merchant_name !== txn.description && (
                  <p className="mt-1 text-xs text-ink-faint">{txn.merchant_name}</p>
                )}
              </div>
              <div className="shrink-0 text-right">
                <Money value={txn.amount} signed tone="auto" className="text-sm font-semibold" />
                {txn.bank_label && (
                  <p className="mt-1 max-w-36 truncate text-right text-xs text-ink-faint">{formatAccountName(txn.bank_label)}</p>
                )}
              </div>
              <div className="shrink-0">
                <SignalDecisionControls txn={txn} onDecide={onDecide} isPending={isPending} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AssetSignalsView({ caseId, accountFilters = [] }: AssetSignalsViewProps) {
  const { data, isLoading } = useHiddenAssetSignals(caseId);
  const scanMutation = useRunHiddenAssetScan(caseId);
  const decisionMutation = useHiddenAssetDecision(caseId);
  const [scanResult, setScanResult] = useState<null | { scanned: number; flagged: number }>(null);

  const handleDecide = (transactionId: number, decision: 'confirmed' | 'dismissed') => {
    decisionMutation.mutate({ transactionId, decision });
  };

  const handleScan = () => {
    scanMutation.mutate(undefined, {
      onSuccess: (res) => {
        setScanResult({ scanned: res.scanned, flagged: res.flagged });
      },
    });
  };

  const { byCategory, categories, totalFlagged } = useMemo(() => {
    const raw = data?.by_category ?? {};
    const filtered: Record<string, Transaction[]> = {};
    let total = 0;
    for (const [cat, txns] of Object.entries(raw)) {
      const matched = accountFilters.length > 0
        ? txns.filter((t) => t.bank_label && accountFilters.includes(t.bank_label))
        : txns;
      if (matched.length > 0) {
        filtered[cat] = matched;
        total += matched.length;
      }
    }
    return {
      byCategory: filtered,
      categories: Object.keys(filtered),
      totalFlagged: total,
    };
  }, [data, accountFilters]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="t-card text-green-900">Asset Signals</h2>
          <p className="mt-1 text-sm text-ink-faint">
            Transactions worth investigating as possible signals of assets not reflected in the
            inventory — crypto, storage units, safe deposit boxes, business entities, specialty
            insurance, or loan repayments. These are leads for attorney review, not confirmed findings.
          </p>
        </div>
        <Button
          variant="secondary"
          icon={RefreshCw}
          onClick={handleScan}
          loading={scanMutation.isPending}
          className="shrink-0"
        >
          Scan for Asset Signals
        </Button>
      </div>

      {scanResult && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-100 px-4 py-2 text-sm text-green-800">
          <RefreshCw size={16} className="text-green-700" />
          Scan complete — {scanResult.scanned} transactions checked, {scanResult.flagged} flagged.
        </div>
      )}

      {scanMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
          <AlertTriangle size={16} />
          Re-scan failed. Please try again.
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Skeleton variant="rect" className="h-24 w-full" />
            <Skeleton variant="rect" className="h-24 w-full" />
            <Skeleton variant="rect" className="h-24 w-full" />
          </div>
          <Skeleton variant="rect" className="h-24 w-full" />
        </div>
      ) : totalFlagged === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={AlertTriangle}
            title="No asset signals detected"
            description="Run a re-scan if you have recently processed new statements."
          />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-warn bg-warn-soft p-4 text-center">
              <p className="t-num-hero text-warn">{totalFlagged}</p>
              <p className="mt-1 text-xs text-warn">Flagged Transactions</p>
              {((data?.confirmed_count ?? 0) > 0 || (data?.dismissed_count ?? 0) > 0) && (
                <p className="mt-1 text-2xs text-warn">
                  {data?.confirmed_count ?? 0} confirmed · {data?.dismissed_count ?? 0} dismissed
                </p>
              )}
            </div>
            <div className="rounded-lg border border-warn bg-warn-soft p-4 text-center">
              <p className="t-num-hero text-warn">{categories.length}</p>
              <p className="mt-1 text-xs text-warn">Signal Categories</p>
            </div>
            <div className="rounded-lg border border-warn bg-warn-soft p-4 text-center">
              <p className="t-num-hero text-warn">
                {money(
                  Object.values(byCategory).flat().reduce((s, t) => s + Math.abs(t.amount), 0),
                  { whole: true },
                )}
              </p>
              <p className="mt-1 text-xs text-warn">Total Flagged Spend (for review)</p>
              <p className="mt-1 text-2xs text-warn">Transaction totals, not appraised asset values</p>
            </div>
          </div>

          <div className="space-y-4">
            {categories.map((cat) => (
              <CategoryGroup
                key={cat}
                category={cat}
                transactions={byCategory[cat] ?? []}
                onDecide={handleDecide}
                isPending={decisionMutation.isPending}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
